// Moteur de clauses du contrat annuel (refonte Contrats, 2026-10-28) : voir docs/AUDIT-CONTRATS-LEGAL.md.
//
// Principe d'Emeni : tout est configurable par l'établissement, DANS DES GARDE-FOUS. Trois niveaux :
//   « impose » : texte fourni, ne se désactive pas (identité, prix, médiation et rétractation d'un particulier…) ;
//   « borne »  : choix d'une formulation ou d'un chiffre entre un minimum et un maximum sûrs ;
//   « libre »  : texte écrit par l'établissement (« Conditions particulières »), avec avertissement.
// Deux jeux : `part` (consommateur) et `pro` (entre professionnels). Un contrat de particulier ne peut pas être
// enregistré avec une clause manifestement interdite (voir `verifierTexteLibre`).
//
// Module PUR (aucun accès base, aucun réseau) : utilisable côté serveur et côté navigateur, testé par
// functions/api/modules/contratClauses.test.js.
//
// ATTENTION : les textes et les bornes ci-dessous sont des propositions de rédaction, PAS un avis juridique.
// Ils doivent être relus par un juriste avant la mise en production (docs/AUDIT-CONTRATS-LEGAL.md, section 4).

export const PROFILS = ['part', 'pro'];
export const NIVEAUX = { IMPOSE: 'impose', BORNE: 'borne', LIBRE: 'libre' };

// Bornes sûres (réglables dans ces limites). Particulier : plus protecteur.
export const BORNES = {
    reclamation_jours: { part: { min: 7, max: 60, defaut: 14 }, pro: { min: 2, max: 60, defaut: 8 } },
    indemnite_pourcent: { part: { min: 0, max: 30, defaut: 0 }, pro: { min: 0, max: 100, defaut: 30 } },
    preavis_jours: { part: { min: 7, max: 60, defaut: 30 }, pro: { min: 7, max: 180, defaut: 30 } },
    information_reconduction_jours: { part: { min: 30, max: 90, defaut: 60 }, pro: { min: 0, max: 90, defaut: 30 } },
};

// Chaque clause : formulations (texte avec jetons {{…}}), niveau, profils concernés.
// Jetons : {{entreprise_nom}} {{nom_client}} {{formule}} {{duree_engagement}} {{jours_reclamation}} {{pourcent_indemnite}}
//          {{preavis_jours}} {{information_jours}} {{mediateur}} {{conditions_url}}
export const CLAUSES = [
    {
        id: 'objet', titre: 'Objet du contrat', niveau: NIVEAUX.IMPOSE, profils: ['part', 'pro'],
        formulations: { standard: "Le présent contrat a pour objet la réalisation par {{entreprise_nom}} de prestations régulières selon la formule « {{formule}} » souscrite par {{nom_client}}, aux dates et conditions précisées au tableau des passages." },
    },
    {
        id: 'duree', titre: 'Durée et reconduction', niveau: NIVEAUX.BORNE, profils: ['part', 'pro'],
        formulations: {
            sans_reconduction: { part: "Le contrat est conclu pour une durée de {{duree_engagement}}. Il prend fin à son terme, sans reconduction automatique.", pro: "Le contrat est conclu pour une durée de {{duree_engagement}}. Il prend fin à son terme, sans reconduction automatique." },
            reconduction: {
                part: "Le contrat est conclu pour une durée de {{duree_engagement}}. Il est reconduit pour une durée identique, sauf opposition du Client. {{entreprise_nom}} informe le Client par écrit, entre {{information_jours}} jours et un mois avant le terme, de la possibilité de ne pas reconduire le contrat. À défaut de cette information, le Client peut résilier le contrat reconduit à tout moment, sans frais, avec un préavis maximal de un mois à compter de sa demande. La résiliation peut être faite par écrit, y compris en ligne depuis l'espace client.",
                pro: "Le contrat est conclu pour une durée de {{duree_engagement}}. Il est reconduit tacitement pour des périodes successives de même durée, sauf dénonciation par écrit au moins {{preavis_jours}} jours avant l'échéance.",
            },
        },
        defaut: { part: 'reconduction', pro: 'reconduction' },
        parametres: { part: ['information_reconduction_jours'], pro: ['preavis_jours'] },
    },
    {
        id: 'resiliation', titre: 'Résiliation anticipée', niveau: NIVEAUX.BORNE, profils: ['part', 'pro'],
        formulations: {
            realisees: { part: "Le Client peut mettre fin au contrat avant son terme par écrit. Les prestations déjà réalisées restent dues ; aucune autre somme n'est exigée, et les remises accordées pour les prestations réalisées restent acquises.", pro: "Le Client peut mettre fin au contrat avant son terme par écrit. Les prestations déjà réalisées restent dues." },
            indemnite: { part: "En cas de résiliation anticipée par le Client, les prestations déjà réalisées restent dues et une indemnité égale à {{pourcent_indemnite}} % du prix des prestations restant à réaliser est due. Cette indemnité est un plafond ; elle ne s'applique pas en cas de manquement de {{entreprise_nom}}.", pro: "En cas de résiliation anticipée par le Client avant le terme de {{duree_engagement}}, les prestations déjà réalisées restent dues et une indemnité égale à {{pourcent_indemnite}} % des échéances restantes est due. Les remises accordées pourront être réajustées au tarif catalogue pour les prestations déjà réalisées." },
        },
        defaut: { part: 'realisees', pro: 'indemnite' },
        parametres: { part: ['indemnite_pourcent'], pro: ['indemnite_pourcent'] },
    },
    {
        id: 'reclamation', titre: 'Garanties et réclamations', niveau: NIVEAUX.BORNE, profils: ['part', 'pro'],
        formulations: {
            standard: {
                part: "Toute réserve sur une intervention doit être signalée à {{entreprise_nom}} dans un délai de {{jours_reclamation}} jours après la prestation, afin qu'elle puisse être constatée. Ce délai ne limite pas les garanties légales dont le Client bénéficie.",
                pro: "Toute réserve sur une intervention doit être formulée par écrit dans un délai de {{jours_reclamation}} jours après la réalisation de la prestation.",
            },
        },
        parametres: { part: ['reclamation_jours'], pro: ['reclamation_jours'] },
    },
    {
        id: 'acces', titre: "Accès et conditions d'intervention", niveau: NIVEAUX.BORNE, profils: ['part', 'pro'],
        formulations: { standard: "Le Client permet l'accès aux lieux aux jours et heures convenus et fournit l'eau et l'électricité nécessaires à l'intervention. Si l'intervention ne peut avoir lieu du fait du Client, un nouveau rendez-vous est convenu." },
    },
    {
        id: 'supplementaires', titre: 'Prestations supplémentaires', niveau: NIVEAUX.BORNE, profils: ['part', 'pro'], facultative: true,
        formulations: { standard: "Toute prestation demandée en dehors de la formule « {{formule}} » fait l'objet d'un devis ou d'une facture distincte, au tarif en vigueur, accepté par le Client avant l'intervention." },
    },
    {
        id: 'paiement', titre: 'Prix et paiement', niveau: NIVEAUX.IMPOSE, profils: ['part', 'pro'],
        formulations: {
            standard: {
                part: "Le prix total TTC et les modalités de paiement choisies par le Client figurent au contrat. Aucun prélèvement n'a lieu avant l'expiration du délai de rétractation, sauf demande expresse d'exécution anticipée.",
                pro: "Le prix et les modalités de paiement figurent au contrat. Toute somme non payée à l'échéance porte intérêt de retard au taux légal en vigueur, majoré selon les règles applicables, et donne lieu à une indemnité forfaitaire pour frais de recouvrement de 40 €.",
            },
        },
    },
    {
        id: 'retractation', titre: 'Droit de rétractation', niveau: NIVEAUX.IMPOSE, profils: ['part'],
        formulations: { standard: { part: "Lorsque le contrat est conclu à distance ou hors établissement, le Client dispose d'un délai de 14 jours pour se rétracter sans motif, en utilisant le formulaire type joint ou toute déclaration dénuée d'ambiguïté. Si le Client demande expressément que l'intervention commence avant la fin de ce délai, il paie la part de prestation déjà réalisée en cas de rétractation." } },
    },
    {
        id: 'mediation', titre: 'Médiation de la consommation', niveau: NIVEAUX.IMPOSE, profils: ['part'],
        formulations: { standard: { part: "En cas de litige non résolu avec {{entreprise_nom}}, le Client peut recourir gratuitement au médiateur de la consommation suivant : {{mediateur}}." } },
    },
    {
        id: 'cgv', titre: 'Conditions générales de vente', niveau: NIVEAUX.BORNE, profils: ['part', 'pro'], facultative: true,
        formulations: { standard: "Les conditions générales de vente de {{entreprise_nom}}, remises au Client avant la signature{{conditions_url}}, font partie du contrat. En cas de contradiction, les stipulations du présent contrat l'emportent." },
    },
    {
        id: 'donnees', titre: 'Données personnelles', niveau: NIVEAUX.IMPOSE, profils: ['part', 'pro'],
        formulations: { standard: "Les données personnelles du Client sont traitées par {{entreprise_nom}} pour l'exécution du contrat, selon sa politique de confidentialité. Le Client dispose des droits d'accès, de rectification, d'effacement et d'opposition." },
    },
    {
        id: 'particulieres', titre: 'Conditions particulières', niveau: NIVEAUX.LIBRE, profils: ['part', 'pro'], facultative: true,
        formulations: { libre: '' },
    },
];

const LONGUEUR_MAX_LIBRE = 4000;
// Clauses réglables où l'établissement peut écrire « son texte » (formulation 'perso'), par exemple en reprenant l'ancien modèle.
// Les clauses imposées (objet, prix, rétractation, médiation, données) ne se réécrivent jamais.
export const CLAUSES_PERSO = ['duree', 'resiliation', 'reclamation', 'acces', 'supplementaires'];
const accepteTextePerso = (id) => CLAUSES_PERSO.includes(id);
const clamp = (v, b) => { const n = Math.round(Number(v)); return Number.isFinite(n) ? Math.max(b.min, Math.min(b.max, n)) : b.defaut; };
const clausesDuProfil = (profil) => CLAUSES.filter((c) => c.profils.includes(profil));
const texteFormulation = (clause, formulation, profil) => {
    const f = clause.formulations[formulation];
    return typeof f === 'string' ? f : (f && f[profil]) || '';
};

/** Choix par défaut d'un type de client (tout activé sauf les clauses facultatives). */
export function choixParDefaut(profil) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const choix = {};
    for (const c of clausesDuProfil(p)) {
        const noms = Object.keys(c.formulations);
        choix[c.id] = {
            actif: !c.facultative || c.id === 'cgv',
            formulation: (c.defaut && c.defaut[p]) || noms[0],
            ...(c.id === 'particulieres' ? { texte: '' } : {}),
        };
    }
    const params = {};
    for (const k of Object.keys(BORNES)) params[k] = BORNES[k][p].defaut;
    // moteur_actif : tant qu'il est faux, les contrats continuent d'utiliser le modèle actuel (Contrats annuels › Modèles). Désactivé
    // par défaut : on ne change aucun contrat existant avant que l'établissement (et son juriste) n'ait relu les clauses.
    return { profil: p, moteur_actif: false, clauses: choix, parametres: params, renvoi_cgv_url: '' };
}

/** Nettoie les choix reçus : formulations inconnues remplacées, chiffres bornés, clauses imposées toujours actives. */
export function normaliserChoix(brut, profil) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const d = choixParDefaut(p);
    const b = brut && typeof brut === 'object' ? brut : {};
    const out = { profil: p, moteur_actif: !!b.moteur_actif, clauses: {}, parametres: {}, renvoi_cgv_url: String(b.renvoi_cgv_url || '').trim().slice(0, 300), formules: {} };
    for (const c of clausesDuProfil(p)) {
        const bc = (b.clauses && b.clauses[c.id]) || {};
        const noms = Object.keys(c.formulations);
        const perso = accepteTextePerso(c.id) && bc.formulation === 'perso';
        const formulation = perso ? 'perso' : (noms.includes(bc.formulation) ? bc.formulation : d.clauses[c.id].formulation);
        const actif = c.niveau === NIVEAUX.IMPOSE ? true : (bc.actif === undefined ? d.clauses[c.id].actif : !!bc.actif);
        out.clauses[c.id] = { actif, formulation, ...(c.id === 'particulieres' || perso ? { texte: String(bc.texte || '').slice(0, LONGUEUR_MAX_LIBRE) } : {}) };
    }
    for (const k of Object.keys(BORNES)) out.parametres[k] = clamp(b.parametres && b.parametres[k] !== undefined ? b.parametres[k] : d.parametres[k], BORNES[k][p]);
    // Exceptions par formule : seules les différences avec le texte de base sont gardées ; une clause imposée ne change jamais.
    const bf = b.formules && typeof b.formules === 'object' ? b.formules : {};
    for (const id of Object.keys(bf).slice(0, 60)) {
        const e = bf[id] && typeof bf[id] === 'object' ? bf[id] : {};
        const ex = { clauses: {}, parametres: {} };
        for (const c of clausesDuProfil(p)) {
            const ec = e.clauses && e.clauses[c.id];
            if (!ec || c.niveau === NIVEAUX.IMPOSE) continue;
            const noms = Object.keys(c.formulations);
            const o = {};
            if (ec.actif !== undefined) o.actif = !!ec.actif;
            if (ec.formulation === 'perso' && accepteTextePerso(c.id)) { o.formulation = 'perso'; o.texte = String(ec.texte || '').slice(0, LONGUEUR_MAX_LIBRE); }
            else if (noms.includes(ec.formulation) && c.id !== 'particulieres') o.formulation = ec.formulation;
            if (c.id === 'particulieres' && ec.texte !== undefined) o.texte = String(ec.texte || '').slice(0, LONGUEUR_MAX_LIBRE);
            if (Object.keys(o).length) ex.clauses[c.id] = o;
        }
        for (const k of Object.keys(BORNES)) if (e.parametres && e.parametres[k] !== undefined) ex.parametres[k] = clamp(e.parametres[k], BORNES[k][p]);
        if (Object.keys(ex.clauses).length || Object.keys(ex.parametres).length) out.formules[String(id).slice(0, 80)] = ex;
    }
    return out;
}

/** Les choix qui valent pour UNE formule : le texte de base, puis l'exception de cette formule s'il y en a une. */
export function choixPourFormule(choix, formuleId) {
    const ex = formuleId && choix.formules ? choix.formules[formuleId] : null;
    if (!ex) return choix;
    const clauses = {};
    for (const id of Object.keys(choix.clauses)) {
        const o = ex.clauses[id];
        const base = choix.clauses[id];
        // Une exception qui change de formulation repart de son propre texte : le texte perso de la base ne la suit pas.
        const sansTexte = o && o.formulation && o.formulation !== 'perso' ? { texte: undefined } : {};
        clauses[id] = o ? { ...base, ...sansTexte, ...o } : base;
    }
    return { ...choix, clauses, parametres: { ...choix.parametres, ...ex.parametres } };
}

// Clauses dont la présence dans un contrat de PARTICULIER est jugée manifestement abusive ou contraire à la loi
// (liste volontairement courte et prudente : un filtre d'alerte, pas une garantie juridique).
const MOTIFS_INTERDITS_PARTICULIER = [
    [/renonc\w*\s+(?:à|a)\s+(?:toute\s+)?(?:garantie|recours|r[ée]clamation|m[ée]diation)/i, "Le client ne peut pas renoncer à ses garanties, à ses recours ni à la médiation."],
    [/(?:aucune?|pas\s+de)\s+(?:r[ée]siliation|r[ée]tractation|remboursement)\s+(?:n'?est\s+)?(?:possible|admise|accept[ée]e)/i, "La résiliation ou la rétractation ne peut pas être exclue pour un particulier."],
    [/tribunal\s+(?:de\s+\w+\s+)?(?:est\s+)?(?:seul\s+)?comp[ée]tent|juridiction\s+exclusive/i, "Une juridiction exclusive ne peut pas être imposée à un particulier."],
    [/modifier\s+(?:unilat[ée]ralement|librement)\s+(?:le\s+)?(?:prix|tarif|contrat)/i, "Le prix et le contrat ne peuvent pas être modifiés unilatéralement par le professionnel."],
];

/** Vérifie un texte libre. Retourne la liste des problèmes (vide = rien d'évident). Pour un professionnel : aucune restriction. */
export function verifierTexteLibre(profil, texte) {
    const t = String(texte || '');
    const problemes = [];
    if (t.length > LONGUEUR_MAX_LIBRE) problemes.push(`Texte trop long (${LONGUEUR_MAX_LIBRE} caractères au plus).`);
    if (profil === 'part') for (const [re, msg] of MOTIFS_INTERDITS_PARTICULIER) if (re.test(t)) problemes.push(msg);
    return problemes;
}

/**
 * Vérifie « Mon texte » d'une clause (formulation 'perso'). Retourne la liste des problèmes : vide = rien d'évident.
 * Délai de réclamation sous le minimum du type de client, ou, pour un particulier, résiliation qui laisse tout le prix dû.
 * Filtre d'alerte prudent, pas une garantie juridique.
 */
export function verifierTextePerso(profil, clauseId, texte) {
    const t = String(texte == null ? '' : texte);
    if (!t.trim()) return ['Le texte est vide.'];
    const problemes = [...verifierTexteLibre(profil, t)];
    if (clauseId === 'reclamation') {
        const min = BORNES.reclamation_jours[profil].min;
        const m = t.match(/(\d+)\s*(heures?|h\b|jours?|j\b)/i);
        if (m) {
            const enHeures = /^h/i.test(m[2]);
            const jours = enHeures ? Number(m[1]) / 24 : Number(m[1]);
            if (jours < min) problemes.push(`Délai de réclamation trop court (${m[1]} ${enHeures ? 'heures' : 'jours'}) : ${min} jours au minimum pour ${profil === 'part' ? 'un particulier' : 'un professionnel'}.`);
        }
    }
    if (clauseId === 'resiliation' && profil === 'part' && /(?:int[ée]gralit[ée]|totalit[ée])[^.]{0,80}(?:reste|restent|due|dus|dues|d[ûu])/i.test(t)) {
        problemes.push("Un particulier ne peut pas devoir l'intégralité du prix en cas de résiliation : seules les prestations réalisées restent dues.");
    }
    return problemes;
}

/**
 * Contrôle STRICT d'un jeu de choix tel que reçu (avant tout nettoyage) : chiffres hors limites, textes libres ou « Mon texte »
 * interdits, dans le texte de base comme dans les exceptions par formule. Retourne la liste des erreurs ; vide = enregistrable.
 * Contrairement à normaliserChoix (qui ramène les chiffres dans les bornes), ici une valeur hors limite est REFUSÉE.
 */
export function verifierChoix(brut, profil) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const b = brut && typeof brut === 'object' ? brut : {};
    const erreurs = [];
    const libelles = { reclamation_jours: 'Délai de réclamation', indemnite_pourcent: "Pourcentage d'indemnité", preavis_jours: 'Préavis', information_reconduction_jours: 'Information avant reconduction' };
    const controleParams = (params, ou) => {
        if (!params) return;
        for (const k of Object.keys(BORNES)) {
            if (params[k] === undefined) continue;
            const n = Number(params[k]);
            const bo = BORNES[k][p];
            if (!Number.isFinite(n) || n < bo.min || n > bo.max) erreurs.push(`${libelles[k]}${ou} : entre ${bo.min} et ${bo.max}.`);
        }
    };
    const controleClauses = (clauses, ou) => {
        for (const c of clausesDuProfil(p)) {
            const ch = clauses && clauses[c.id];
            if (!ch || ch.actif === false) continue;
            if (c.id === 'particulieres' && String(ch.texte || '').trim()) for (const m of verifierTexteLibre(p, ch.texte)) erreurs.push(`Conditions particulières${ou} : ${m}`);
            if (ch.formulation === 'perso' && accepteTextePerso(c.id)) for (const m of verifierTextePerso(p, c.id, ch.texte)) erreurs.push(`${c.titre}${ou} : ${m}`);
        }
    };
    controleParams(b.parametres, '');
    controleClauses(b.clauses, '');
    const bf = b.formules && typeof b.formules === 'object' ? b.formules : {};
    for (const id of Object.keys(bf)) { const e = bf[id] || {}; controleParams(e.parametres, ' (exception de formule)'); controleClauses(e.clauses, ' (exception de formule)'); }
    return erreurs;
}

/**
 * Reprise d'un ancien modèle de contrat (5 zones de texte). Chaque texte devient « Mon texte » de la clause correspondante ; une
 * zone vide laisse la clause à son réglage actuel. Retourne { choix, verdicts } : un verdict par clause reprise ('repris' ou 'bloque'
 * avec les raisons). Rien n'est enregistré ici : tant qu'un verdict est 'bloque', verifierChoix refuse le résultat.
 */
const CORRESPONDANCE_MODELE = { duree: 'clause_duree_renouvellement', acces: 'clause_acces_intervention', reclamation: 'clause_garantie', resiliation: 'clauses_resiliation', supplementaires: 'clause_prestations_supplementaires' };
export function reprendreModele(profil, choixBruts, modele) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const choix = normaliserChoix(choixBruts, p);
    const m = modele && typeof modele === 'object' ? modele : {};
    const verdicts = [];
    for (const c of clausesDuProfil(p)) {
        const champ = CORRESPONDANCE_MODELE[c.id];
        const texte = champ ? String(m[champ] || '').trim() : '';
        if (!texte) continue;
        choix.clauses[c.id] = { actif: true, formulation: 'perso', texte: texte.slice(0, LONGUEUR_MAX_LIBRE) };
        const problemes = verifierTextePerso(p, c.id, texte);
        verdicts.push({ id: c.id, titre: c.titre, statut: problemes.length ? 'bloque' : 'repris', problemes });
    }
    return { choix, verdicts };
}

const remplacer = (texte, vars) => String(texte || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (m, k) => (vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : m));

/**
 * Assemble les clauses d'un contrat dans l'ordre. `contexte` : { entreprise_nom, nom_client, formule, duree_engagement,
 * mediateur }. Retourne { clauses: [{id, titre, niveau, texte}], manques: [{code, message, bloquant}] }.
 */
export function assembler(profil, choixBruts, contexte) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const ctx = contexte || {};
    // Les exceptions de la formule du contrat s'appliquent par-dessus le texte de base.
    const choix = choixPourFormule(normaliserChoix(choixBruts, p), ctx.formule_id);
    const manques = [];
    const vars = {
        entreprise_nom: ctx.entreprise_nom || '{{entreprise_nom}}',
        nom_client: ctx.nom_client || '{{nom_client}}',
        formule: ctx.formule || '{{formule}}',
        duree_engagement: ctx.duree_engagement || '{{duree_engagement}}',
        jours_reclamation: choix.parametres.reclamation_jours,
        pourcent_indemnite: choix.parametres.indemnite_pourcent,
        preavis_jours: choix.parametres.preavis_jours,
        information_jours: choix.parametres.information_reconduction_jours,
        mediateur: ctx.mediateur || '',
        conditions_url: choix.renvoi_cgv_url ? ` (${choix.renvoi_cgv_url})` : '',
    };
    const clauses = [];
    for (const c of clausesDuProfil(p)) {
        const ch = choix.clauses[c.id];
        if (!ch.actif) continue;
        const perso = ch.formulation === 'perso' && accepteTextePerso(c.id);
        let texte = c.id === 'particulieres' || perso ? ch.texte : texteFormulation(c, ch.formulation, p);
        if (perso) {
            const pb = verifierTextePerso(p, c.id, texte);
            if (pb.length) manques.push({ code: 'texte_perso', message: `${c.titre} : ${pb.join(' ')}`, bloquant: true });
        }
        if (c.id === 'particulieres') {
            const pb = verifierTexteLibre(p, texte);
            if (pb.length) manques.push({ code: 'texte_libre', message: `Conditions particulières : ${pb.join(' ')}`, bloquant: true });
            if (!String(texte).trim()) continue;
        }
        if (c.id === 'mediation' && !String(ctx.mediateur || '').trim()) {
            manques.push({ code: 'mediateur', message: "Le médiateur de la consommation n'est pas renseigné : il est obligatoire pour un contrat avec un particulier.", bloquant: true });
        }
        clauses.push({ id: c.id, titre: c.titre, niveau: c.niveau, texte: remplacer(texte, vars) });
    }
    return { clauses, manques };
}

/** Contrôle « prêt à envoyer » : ce qui manque pour que le contrat puisse partir. */
export function controlerPret(profil, choixBruts, contexte) {
    const p = PROFILS.includes(profil) ? profil : 'part';
    const ctx = contexte || {};
    const { manques } = assembler(p, choixBruts, ctx);
    const vide = (v) => !String(v == null ? '' : v).trim();
    if (vide(ctx.prestataire_siret)) manques.push({ code: 'siret', message: "Le SIRET de l'établissement n'est pas renseigné (Paramètres de facturation).", bloquant: true });
    if (vide(ctx.client_adresse)) manques.push({ code: 'adresse', message: "L'adresse du client n'est pas renseignée.", bloquant: true });
    if (p === 'pro' && vide(ctx.client_siret)) manques.push({ code: 'siret_client', message: "Le SIRET du client professionnel n'est pas renseigné.", bloquant: false });
    if (p === 'pro' && vide(ctx.client_representant)) manques.push({ code: 'representant', message: 'Le représentant du client professionnel n\'est pas renseigné.', bloquant: false });
    if (p === 'part' && ctx.tva_franchise === undefined) manques.push({ code: 'tva', message: "Indiquez si l'établissement est en franchise de TVA (la mention légale s'ajoute alors au contrat).", bloquant: false });
    return { pret: !manques.some((m) => m.bloquant), manques };
}
